import React from 'react'
import { Button, Element, Message } from 'react-bulma-components'
import {
  MissionContractModel,
  PeriodSummaryStatus,
} from '../../../../../backend/src/services/resources/period-summaries/period-summary.types'
import { PeriodSummary } from '../../../api/period-summaries'
import Protected from '../../../components/protected/protected'
import { useModal } from '../../../components/modal'
import useStore from '../../../store'
import { SessionSlice } from '../../../store/session'
import { useResolveCancellationMutation } from '../../../queries/period-summaries'
import { ResolveCancellationModal } from './pre-billing-cancellation-modals'
import { ResolveCancellationFormValues } from './pre-billing-cancellation-modals.types'
import Avatar from '../../../components/avatar/avatar'
import { localDate } from '../../../utils/date'

export interface PreBillingStatusBannerProps {
  preBilling: PeriodSummary
}

/**
 * Three-parties pre-billing: status banner, request / resolve actions, and cancellation history.
 */
const PreBillingStatusBanner: React.FC<PreBillingStatusBannerProps> = ({ preBilling }) => {
  const currentUserRole = useStore(
    ({ session }) => session.currentUserRole,
  ) as SessionSlice['currentUserRole']

  const resolveModal = useModal()

  const resolveMutation = useResolveCancellationMutation()

  if (preBilling.contractModel !== MissionContractModel.threeParties) {
    return null
  }

  const status = preBilling.status ?? PeriodSummaryStatus.active
  const pending = preBilling.cancellationRequest

  const isRequester = pending && currentUserRole === pending.requesterRole

  const handleResolveSubmit = async (values: ResolveCancellationFormValues): Promise<void> => {
    await resolveMutation.mutateAsync({
      id: preBilling._id,
      decision: values.decision,
      resolutionComment: values.resolutionComment.trim(),
    })
  }

  return (
    <>
      {status === PeriodSummaryStatus.cancellationRequested && pending && (
        <Message color="warning" mb={6} p={4} data-test="pre-billing-cancellation-pending-banner">
          <Message.Body>
            {isRequester ? (
              <>
                <Element renderAs="p">
                  <strong>Demande d&apos;annulation en cours - en attente de réponse</strong>
                </Element>
                <Element className="is-flex is-align-items-center" style={{ gap: 8 }}>
                  <Avatar user={pending.requestedBy as any} />:
                  <Element renderAs="span">
                    {localDate(pending.requestedAt)} — <em>{pending.requestComment}</em>
                  </Element>
                </Element>
              </>
            ) : (
              <Element
                className="is-flex is-align-items-center is-justify-content-space-between"
                style={{ flexWrap: 'wrap', gap: '1rem' }}
              >
                <Element style={{ flex: '1 1 12rem', minWidth: 0 }}>
                  <Element renderAs="p">
                    <strong>Demande d&apos;annulation en cours - en attente de réponse</strong>
                  </Element>
                  <Element renderAs="p">
                    <Avatar user={pending.requestedBy as any} /> de{' '}
                    {pending.requesterRole === 'employerMember'
                      ? preBilling.employer.name
                      : preBilling.interimAgency.name}{' '}
                    souhaite annuler cette pré-facturation.
                  </Element>
                  <Element className="is-flex is-align-items-center" style={{ gap: 8 }}>
                    <Element renderAs="span">
                      {localDate(pending.requestedAt)} — <em>{pending.requestComment}</em>
                    </Element>
                  </Element>
                </Element>
                <Protected
                  roles={[
                    {
                      role: 'employerMember',
                      action: { resource: 'periodSummaries', name: 'resolveCancellationRequest' },
                    },
                    'interimAgencyMember',
                  ]}
                >
                  <Button
                    color="warning"
                    style={{ flexShrink: 0, alignSelf: 'center' }}
                    onClick={() => resolveModal.setIsDisplayed(true)}
                    data-test="pre-billing-resolve-cancellation-trigger"
                  >
                    Répondre
                  </Button>
                </Protected>
              </Element>
            )}
          </Message.Body>
        </Message>
      )}

      {status === PeriodSummaryStatus.cancelled && (
        <Message color="warning" mb={4} p={4} data-test="pre-billing-cancelled-banner">
          <Message.Body>
            <strong>Cette pré-facturation a été annulée.</strong>
          </Message.Body>
        </Message>
      )}

      <ResolveCancellationModal actions={resolveModal} onSubmit={handleResolveSubmit} />
    </>
  )
}

export default PreBillingStatusBanner
